import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { coachHours, sessionCount } from '../utils/coachHours';
import {
  collection, getDocs, query, where, doc, updateDoc, addDoc, serverTimestamp
} from 'firebase/firestore';
import Layout from '../components/Layout';
import {
  Box, Typography, Paper, Button, TextField, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, Alert, CircularProgress, Dialog, DialogTitle, DialogContent,
  DialogActions, Grid, Divider, Avatar
} from '@mui/material';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CalculateIcon from '@mui/icons-material/Calculate';

const PAYMENT_METHODS = ['Venmo', 'Zelle'];

function CoachPayments() {
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [payDialog, setPayDialog] = useState(false);
  const [selectedCoach, setSelectedCoach] = useState(null);
  const [payForm, setPayForm] = useState({ amount: '', method: 'Venmo', note: '' });
  const [saving, setSaving] = useState(false);
  const [calcDialog, setCalcDialog] = useState(false);
  const [calcCoach, setCalcCoach] = useState(null);
  const [calcSessions, setCalcSessions] = useState({ head: 0, assistant: 0, headHours: 0, assistantHours: 0 });

  useEffect(() => {
    fetchCoaches();
  }, []);

  const fetchCoaches = async () => {
    setLoading(true);
    const coachSnap = await getDocs(collection(db, 'coaches'));
    const coachList = coachSnap.docs.map(d => ({ id: d.id, ...d.data() }))
      .filter(c => !c.isVolunteer)
      .sort((a, b) => (a.lastName || '').localeCompare(b.lastName || ''));
    setCoaches(coachList);
    setLoading(false);
  };

  const calcOwed = (coach) => {
    const headRate = parseFloat(coach.headRatePerHour) || 0;
    const assistRate = parseFloat(coach.assistantRatePerHour) || 0;
    const headSessions = coach.totalSessionsLed || 0;
    const assistSessions = coach.totalAssistSessions || 0;
    // Assume 1 hour per session as default — can be overridden
    return (headSessions * headRate) + (assistSessions * assistRate);
  };

  const balance = (coach) => {
    const owed = coach.totalOwed ?? calcOwed(coach);
    const paid = coach.totalPaid || 0;
    return owed - paid;
  };

  const openCalc = async (coach) => {
    setCalcCoach(coach);
    // Fetch their classes to count sessions
    const [headSnap, assistSnap] = await Promise.all([
      getDocs(query(collection(db, 'classes'), where('headCoachId', '==', coach.id))),
      getDocs(query(collection(db, 'classes'), where('assistantCoachIds', 'array-contains', coach.id))),
    ]);
    const headClasses = headSnap.docs.map(d => d.data()).filter(c => c.status !== 'canceled');
    const assistClasses = assistSnap.docs.map(d => d.data()).filter(c => c.status !== 'canceled');
    // Counted per session, not per document: a class covering two courts is two
    // documents but one coaching session. See src/utils/coachHours.js.
    setCalcSessions({
      head: sessionCount(headClasses),
      assistant: sessionCount(assistClasses),
      headHours: Math.round(coachHours(headClasses) * 10) / 10,
      assistantHours: Math.round(coachHours(assistClasses) * 10) / 10,
    });
    setCalcDialog(true);
  };

  const applyCalc = async () => {
    const headRate = parseFloat(calcCoach.headRatePerHour) || 0;
    const assistRate = parseFloat(calcCoach.assistantRatePerHour) || 0;
    const totalOwed = Math.round(((calcSessions.headHours * headRate) + (calcSessions.assistantHours * assistRate)) * 100) / 100;
    await updateDoc(doc(db, 'coaches', calcCoach.id), {
      totalOwed,
      totalSessionsLed: calcSessions.head,
      totalAssistSessions: calcSessions.assistant,
    });
    setCoaches(prev => prev.map(c => c.id === calcCoach.id
      ? { ...c, totalOwed, totalSessionsLed: calcSessions.head, totalAssistSessions: calcSessions.assistant }
      : c
    ));
    setCalcDialog(false);
    setSuccess(`Updated amount owed for ${calcCoach.firstName} ${calcCoach.lastName}: $${totalOwed}`);
    setTimeout(() => setSuccess(''), 4000);
  };

  const openPay = (coach) => {
    setSelectedCoach(coach);
    setPayForm({ amount: String(Math.max(0, balance(coach)).toFixed(2)), method: 'Venmo', note: '' });
    setPayDialog(true);
  };

  const handlePay = async () => {
    if (!payForm.amount || parseFloat(payForm.amount) <= 0) {
      setError('Please enter a valid amount.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const amount = parseFloat(payForm.amount);
      const newTotalPaid = (selectedCoach.totalPaid || 0) + amount;
      await updateDoc(doc(db, 'coaches', selectedCoach.id), {
        totalPaid: newTotalPaid,
        lastPaidAt: serverTimestamp(),
        lastPaymentMethod: payForm.method,
      });
      await addDoc(collection(db, 'coachPayments'), {
        coachId: selectedCoach.id,
        coachName: `${selectedCoach.firstName} ${selectedCoach.lastName}`,
        amount,
        method: payForm.method,
        note: payForm.note,
        paidAt: serverTimestamp(),
      });
      setCoaches(prev => prev.map(c => c.id === selectedCoach.id
        ? { ...c, totalPaid: newTotalPaid, lastPaymentMethod: payForm.method }
        : c
      ));
      setPayDialog(false);
      setSuccess(`Payment of $${amount.toFixed(2)} recorded for ${selectedCoach.firstName} ${selectedCoach.lastName}.`);
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setError('Failed to record payment. Please try again.');
      console.error(err);
    }
    setSaving(false);
  };

  const initials = (c) => `${c.firstName?.charAt(0) || ''}${c.lastName?.charAt(0) || ''}`.toUpperCase();

  if (loading) return (
    <Layout><Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box></Layout>
  );

  const totalOwedAll = coaches.reduce((sum, c) => sum + Math.max(0, balance(c)), 0);

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1E3A5F' }}>Coach Payments</Typography>
            <Typography variant="body1" sx={{ color: '#888', mt: 0.5 }}>
              Track and record coach compensation via Venmo / Zelle.
            </Typography>
          </Box>
          <Paper elevation={2} sx={{ p: 2, borderRadius: 2, textAlign: 'center', minWidth: 160 }}>
            <Typography variant="caption" sx={{ color: '#888' }}>Total Outstanding</Typography>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: totalOwedAll > 0 ? '#E67E22' : '#27AE60' }}>
              ${totalOwedAll.toFixed(2)}
            </Typography>
          </Paper>
        </Box>

        {success && <Alert severity="success" sx={{ mb: 3 }}>{success}</Alert>}
        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

        <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#1E3A5F' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Coach</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">Head Sessions</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">Asst Sessions</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">Rate (Head/Asst)</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">Total Owed</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">Total Paid</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">Balance</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {coaches.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4, color: '#aaa' }}>No coaches found.</TableCell>
                  </TableRow>
                ) : coaches.map((coach, i) => {
                  const bal = balance(coach);
                  return (
                    <TableRow key={coach.id} sx={{ backgroundColor: i % 2 === 0 ? '#f9f9f9' : 'white' }}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar sx={{ width: 32, height: 32, backgroundColor: '#1E3A5F', fontSize: '0.8rem' }}>
                            {initials(coach)}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{coach.firstName} {coach.lastName}</Typography>
                            <Typography variant="caption" sx={{ color: '#888' }}>{coach.role || 'Coach'}</Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell align="center">{coach.totalSessionsLed || 0}</TableCell>
                      <TableCell align="center">{coach.totalAssistSessions || 0}</TableCell>
                      <TableCell align="right" sx={{ color: '#555' }}>
                        ${coach.headRatePerHour || 0} / ${coach.assistantRatePerHour || 0}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>${(coach.totalOwed || 0).toFixed(2)}</TableCell>
                      <TableCell align="right" sx={{ color: '#27AE60', fontWeight: 600 }}>${(coach.totalPaid || 0).toFixed(2)}</TableCell>
                      <TableCell align="right">
                        <Typography sx={{ fontWeight: 700, color: bal > 0 ? '#E67E22' : bal < 0 ? '#E74C3C' : '#888' }}>
                          ${Math.abs(bal).toFixed(2)}{bal < 0 ? ' (overpaid)' : ''}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          <Button size="small" variant="outlined" startIcon={<CalculateIcon />}
                            onClick={() => openCalc(coach)}
                            sx={{ borderColor: '#2E6DA4', color: '#2E6DA4', fontSize: '0.7rem' }}>
                            Calc
                          </Button>
                          <Button size="small" variant="contained" startIcon={<AttachMoneyIcon />}
                            onClick={() => openPay(coach)}
                            disabled={false}
                            sx={{ backgroundColor: '#27AE60', '&:hover': { backgroundColor: '#1E8449' }, fontSize: '0.7rem' }}>
                            Pay
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Calculate Dialog */}
        <Dialog open={calcDialog} onClose={() => setCalcDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ backgroundColor: '#1E3A5F', color: 'white' }}>
            Calculate Amount Owed — {calcCoach?.firstName} {calcCoach?.lastName}
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Typography variant="body2" sx={{ color: '#555', mb: 2 }}>
              Based on all non-canceled classes in the schedule:
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <Paper sx={{ flex: 1, p: 1.5, backgroundColor: '#f0f4f8', borderRadius: 2, textAlign: 'center' }}>
                <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 0.5 }}>Head Coach Sessions</Typography>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1E3A5F' }}>{calcSessions.head}</Typography>
                <Typography variant="caption" sx={{ color: '#888' }}>{calcSessions.headHours} hrs × ${calcCoach?.headRatePerHour || 0}/hr</Typography>
                <Typography variant="body1" sx={{ fontWeight: 700, color: '#1E3A5F', mt: 0.5 }}>
                  = ${(calcSessions.headHours * (calcCoach?.headRatePerHour || 0)).toFixed(2)}
                </Typography>
              </Paper>
              <Paper sx={{ flex: 1, p: 1.5, backgroundColor: '#f0f4f8', borderRadius: 2, textAlign: 'center' }}>
                <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 0.5 }}>Assistant Sessions</Typography>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1E3A5F' }}>{calcSessions.assistant}</Typography>
                <Typography variant="caption" sx={{ color: '#888' }}>{calcSessions.assistantHours} hrs × ${calcCoach?.assistantRatePerHour || 0}/hr</Typography>
                <Typography variant="body1" sx={{ fontWeight: 700, color: '#1E3A5F', mt: 0.5 }}>
                  = ${(calcSessions.assistantHours * (calcCoach?.assistantRatePerHour || 0)).toFixed(2)}
                </Typography>
              </Paper>
            </Box>
            <Divider />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Total Owed</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#E67E22' }}>
                ${(calcSessions.headHours * (calcCoach?.headRatePerHour || 0) + calcSessions.assistantHours * (calcCoach?.assistantRatePerHour || 0)).toFixed(2)}
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setCalcDialog(false)} sx={{ color: '#888' }}>Cancel</Button>
            <Button variant="contained" onClick={applyCalc} sx={{ backgroundColor: '#1E3A5F' }}>
              Save Amount Owed
            </Button>
          </DialogActions>
        </Dialog>

        {/* Pay Dialog */}
        <Dialog open={payDialog} onClose={() => setPayDialog(false)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ backgroundColor: '#27AE60', color: 'white' }}>
            Record Payment — {selectedCoach?.firstName} {selectedCoach?.lastName}
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <Box sx={{ mb: 2, p: 2, backgroundColor: '#f0f4f8', borderRadius: 2 }}>
              <Typography variant="body2" sx={{ color: '#888' }}>Balance Outstanding</Typography>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#E67E22' }}>
                ${Math.max(0, balance(selectedCoach || {})).toFixed(2)}
              </Typography>
            </Box>
            <TextField fullWidth label="Amount *" type="number" value={payForm.amount}
              onChange={e => setPayForm(p => ({ ...p, amount: e.target.value }))}
              inputProps={{ min: 0.01, step: 0.01 }}
              sx={{ mb: 2 }} />
            <TextField fullWidth select label="Payment Method" value={payForm.method}
              onChange={e => setPayForm(p => ({ ...p, method: e.target.value }))}
              sx={{ mb: 2 }}>
              {PAYMENT_METHODS.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
            </TextField>
            <TextField fullWidth label="Note (optional)" value={payForm.note}
              onChange={e => setPayForm(p => ({ ...p, note: e.target.value }))}
              placeholder="e.g. March sessions" sx={{ mb: 2 }} />
            {selectedCoach?.venmoUsername && payForm.method === 'Venmo' && (
              <Button
                fullWidth
                variant="outlined"
                onClick={() => {
                  const username = selectedCoach.venmoUsername.replace('@', '');
                  const note = encodeURIComponent(payForm.note || 'Torch Beach VB coaching payment');
                  const amount = parseFloat(payForm.amount) || 0;
                  window.open(`venmo://paycharge?txn=pay&recipients=${username}&amount=${amount}&note=${note}`, '_blank');
                }}
                sx={{ borderColor: '#3D95CE', color: '#3D95CE', fontWeight: 600 }}
              >
                Open in Venmo (@{selectedCoach.venmoUsername.replace('@', '')})
              </Button>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => { setPayDialog(false); setError(''); }} sx={{ color: '#888' }}>Cancel</Button>
            <Button variant="contained" onClick={handlePay} disabled={saving}
              sx={{ backgroundColor: '#27AE60', '&:hover': { backgroundColor: '#1E8449' } }}>
              {saving ? 'Saving...' : 'Record Payment'}
            </Button>
          </DialogActions>
        </Dialog>

      </Box>
    </Layout>
  );
}

export default CoachPayments;
